import { useEffect } from "react";
import { FileSlim, getFilesByYear } from "./api/api";
import { useFiles } from "./files/filesStore";

export function FileHydrator() {
    useEffect(() => {
        let abort = false;
        (async function () {
            let year: number | null = new Date().getFullYear();
            while (year !== null) {
                const data = await getFilesByYear(year);
                useFiles.getState().hydrateFiles(data.files, false);
                year = data.prevYear;
                if (abort) return;
            }

            // Prune caches
            console.log("Pruning blob cache...");
            const cache = await caches.open("blob-cache");
            const thumbnails = new Set(Object.values(useFiles.getState().files).map(x => x.blobs.thumbnail));
            const cacheKeys = new Set((await cache.keys()).map(r => r.url.split("/").pop()!));
            console.log({ cacheKeys, thumbnails });
            const toBeRemoved = cacheKeys.difference(thumbnails);
            console.log(`${toBeRemoved.size} items to be removed.`);
            for (const key of toBeRemoved) {
                await cache.delete(key);
                if (abort) return;
            }

            // Start loading thumbnails in the background
            console.log("Starting background thumbnail loading...");
            const toBeAdded = thumbnails.difference(cacheKeys).values().toArray();
            console.log(`${toBeAdded.length} items to be added.`);
            let batchSize = 100;
            while (toBeAdded.length > 0) {
                if (abort) return;

                const batch = new Set();
                while (batch.size < batchSize && toBeAdded.length > 0) {
                    const item = toBeAdded.pop();
                    if (!item) continue;
                    // Filter the batch against the cache again - the user might 
                    // have already viewed some of the thumbnails while we were 
                    // waiting for the previous batch to load
                    if (!await cache.match(`/api/blob/${item}`))
                        batch.add(item);
                }

                const controller = navigator.serviceWorker.controller;
                if (!controller) {
                    console.warn("No service worker controller found, cannot bulk fetch blobs.");
                    break;
                }

                let start = Date.now();
                const messageChannel = new MessageChannel();
                const promise = new Promise<void>((resolve) => {
                    messageChannel.port2.onmessage = () => resolve();
                });
                navigator.serviceWorker.controller?.postMessage({
                    type: "CACHE_BLOBS",
                    blobs: batch.values().toArray(),
                    backChannel: messageChannel.port1
                }, [messageChannel.port1]);
                await promise;

                let duration = Date.now() - start;
                console.log(`Fetched batch of thumbnails in ${duration}ms, ${toBeAdded.length} remaining.`);

                // wait for duration * 2, i.e. use only 33% of bandwidth for the background sync
                await new Promise(resolve => setTimeout(resolve, duration * 2));

                const targetDuration = 500;
                const assumedLatency = 20;
                batchSize = batch.size * (targetDuration - assumedLatency) / (duration - assumedLatency);
                batchSize = Math.max(30, batchSize);
                batchSize = Math.min(1000, batchSize);
                batchSize = Math.round(batchSize);
                console.log(`...adjusting batch size to ${batchSize}`);
            }
        })();

        return () => {
            abort = true;
        };
    }, []);

    return false;
}
